/**
 * Score Service - Fetches regulatory pressure scores from backend JSON
 */

export interface ScoreComponents {
  enforcement: number;
  regulatory_change: number;
  rhetoric: number;
  market_stability: number;
}

export interface LatestSignal {
  text: string;
  days_ago: number;
  severity?: number;
  type?: string;
}

/**
 * Regulatory status per gambling category
 */
export interface RegulatoryStatus {
  online_casino: 'open' | 'regulated' | 'restricted' | 'prohibited' | 'unknown';
  online_sports: 'open' | 'regulated' | 'restricted' | 'prohibited' | 'unknown';
  land_casino: 'open' | 'regulated' | 'restricted' | 'prohibited' | 'unknown';
  land_sports: 'open' | 'regulated' | 'restricted' | 'prohibited' | 'unknown';
}

/**
 * Trajectory indicates the direction of regulatory change
 */
export type Trajectory = 'opening' | 'stable' | 'tightening' | 'closing';

/**
 * Opportunity status thresholds (inverted from pressure - higher is better)
 */
export type OpportunityStatus = 'Low' | 'Moderate' | 'Good' | 'Excellent';

export interface CountryScore {
  code: string;
  name: string;
  score: number;
  status: 'Stable' | 'Watch' | 'Tightening' | 'High Pressure';
  delta_30d: number;
  trend: 'up' | 'down' | 'stable';
  drivers: string[];
  latest_signal: LatestSignal;
  components?: ScoreComponents;
  coverage_factor?: number;
  jurisdiction_type?: 'country' | 'us_state' | 'ca_province';
  parent_code?: string;
  // New opportunity score fields
  opportunity_score?: number;
  opportunity_status?: OpportunityStatus;
  trajectory?: Trajectory;
  market_maturity?: number;
  regulatory_status?: RegulatoryStatus | null;
  low_confidence?: boolean;
  stale?: boolean;
  stale_reason?: string;
}

export interface HistoryPoint {
  date: string;
  score: number;
  status: string;
}

export interface Article {
  id: string;
  title: string;
  url: string;
  source: string;
  published_at: string;
  signal?: {
    type: string;
    severity: number;
    explanation: string;
  };
}

export interface ScoresData {
  last_updated: string;
  countries: CountryScore[];
}

/**
 * Fetch latest regulatory pressure scores from Supabase Storage
 * Falls back to local /api path if environment variable not set (for local dev)
 */
export async function fetchLatestScores(): Promise<ScoresData> {
  try {
    // Use Supabase public URL in production, fallback to local for development
    const SUPABASE_SCORES_URL = import.meta.env.VITE_SUPABASE_SCORES_URL || '/api/scores_latest.json';
    // Add cache-busting parameter to bypass CDN/browser cache
    const urlWithCacheBust = `${SUPABASE_SCORES_URL}?t=${Date.now()}`;
    const response = await fetch(urlWithCacheBust);

    if (!response.ok) {
      throw new Error(`Failed to fetch scores: HTTP ${response.status}`);
    }

    const data = await response.json();

    // Validate data structure
    if (!data.last_updated || !Array.isArray(data.countries)) {
      throw new Error('Invalid data format received from backend');
    }

    return data;
  } catch (error) {
    console.error('Error fetching regulatory scores:', error);
    throw error;
  }
}

/**
 * Convert backend country array to a lookup map by country code
 */
export function createScoreMap(scores: ScoresData): Record<string, CountryScore> {
  return Object.fromEntries(
    scores.countries.map(country => [country.code, country])
  );
}

/**
 * Calculate status from score
 */
function calculateStatus(score: number): string {
  if (score < 35) return 'Stable';
  if (score < 55) return 'Watch';
  if (score < 75) return 'Tightening';
  return 'High Pressure';
}

/**
 * History data format from backend scores_history.json
 */
interface HistoryEntry {
  timestamp: string;
  scores: Record<string, number>;
}

interface HistoryData {
  history: HistoryEntry[];
}

/**
 * Fetch historical scores for a jurisdiction from real backend data
 */
export async function fetchJurisdictionHistory(code: string, days: number = 30): Promise<HistoryPoint[]> {
  try {
    // Use Supabase public URL in production, fallback to local for development
    const HISTORY_URL = import.meta.env.VITE_SUPABASE_HISTORY_URL || '/api/scores_history.json';
    const urlWithCacheBust = `${HISTORY_URL}?t=${Date.now()}`;

    const response = await fetch(urlWithCacheBust);

    if (!response.ok) {
      throw new Error(`Failed to fetch history: HTTP ${response.status}`);
    }

    const data: HistoryData = await response.json();

    if (!data.history || !Array.isArray(data.history)) {
      throw new Error('Invalid history data format');
    }

    // Transform history entries to HistoryPoint format for this jurisdiction
    const historyPoints: HistoryPoint[] = data.history
      .filter(entry => entry.scores && entry.scores[code] !== undefined)
      .map(entry => ({
        date: entry.timestamp.split('T')[0],
        score: entry.scores[code],
        status: calculateStatus(entry.scores[code])
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Deduplicate by date (keep latest score for each date)
    const uniqueByDate = new Map<string, HistoryPoint>();
    for (const point of historyPoints) {
      uniqueByDate.set(point.date, point);
    }

    // Return up to requested number of days
    return Array.from(uniqueByDate.values()).slice(-days);
  } catch (error) {
    console.error('Error fetching jurisdiction history:', error);
    // Return empty array on error - component will show appropriate message
    return [];
  }
}

/**
 * Mock articles for fallback when API is not available
 */
function getMockArticles(code: string): Article[] {
  return [
    {
      id: '1',
      title: `Regulatory update for ${code} jurisdiction`,
      url: 'https://example.com/article1',
      source: 'Regulatory News',
      published_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      signal: {
        type: 'regulatory_change',
        severity: 0.7,
        explanation: 'New compliance requirements announced'
      }
    },
    {
      id: '2',
      title: `Market analysis: ${code} gaming sector trends`,
      url: 'https://example.com/article2',
      source: 'iGaming Business',
      published_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      signal: {
        type: 'market_stability',
        severity: 0.4,
        explanation: 'Market conditions remain stable'
      }
    },
    {
      id: '3',
      title: `Enforcement action in ${code}`,
      url: 'https://example.com/article3',
      source: 'Gaming Intelligence',
      published_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      signal: {
        type: 'enforcement',
        severity: 0.8,
        explanation: 'Regulator fines operator for compliance violations'
      }
    }
  ];
}

/**
 * Apply client-side filters to articles
 */
function filterArticles(
  articles: Article[],
  filters: { signalType?: string; days?: number }
): Article[] {
  let filtered = articles;

  if (filters.signalType && filters.signalType !== 'all') {
    filtered = filtered.filter(a => a.signal?.type === filters.signalType);
  }

  if (filters.days) {
    const cutoff = Date.now() - filters.days * 24 * 60 * 60 * 1000;
    filtered = filtered.filter(a => new Date(a.published_at).getTime() >= cutoff);
  }

  return filtered;
}

/**
 * Build PostgREST URL for fetching signals with embedded articles.
 * Queries ai_signals table with article join — only returns classified, relevant articles.
 */
function buildSupabaseArticlesUrl(
  baseUrl: string,
  code: string,
  filters: { signalType?: string; days?: number }
): string {
  const days = filters.days || 30;
  const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

  const params = new URLSearchParams({
    select: '*,articles(id,title,url,published_at,source)',
    jurisdiction_code: `eq.${code}`,
    is_marketing: 'eq.false',
    relevance: 'gte.0.7',
    processed_at: `gte.${cutoff}`,
    order: 'processed_at.desc',
    limit: '50',
  });

  if (filters.signalType && filters.signalType !== 'all') {
    params.set('signal_type', `eq.${filters.signalType}`);
  }

  return `${baseUrl}/rest/v1/ai_signals?${params.toString()}`;
}

/**
 * Transform a PostgREST signal+article response into our Article interface.
 */
function mapSignalToArticle(signal: Record<string, any>): Article {
  const article = signal.articles;
  return {
    id: article?.id || signal.article_id,
    title: article?.title || 'Untitled',
    url: article?.url || '',
    source: article?.source || 'Unknown',
    published_at: article?.published_at || signal.processed_at,
    signal: {
      type: signal.signal_type || 'regulatory_change',
      severity: signal.severity || 0.5,
      explanation: signal.ai_explanation || '',
    },
  };
}

/**
 * Fetch articles for a jurisdiction from Supabase PostgREST API.
 * Falls back to mock data when Supabase is not configured or unavailable.
 */
export async function fetchJurisdictionArticles(
  code: string,
  filters: { signalType?: string; days?: number } = {}
): Promise<Article[]> {
  const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    console.debug('Supabase not configured, using mock articles');
    return filterArticles(getMockArticles(code), filters);
  }

  try {
    const url = buildSupabaseArticlesUrl(SUPABASE_URL, code, filters);

    const response = await fetch(url, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
    });

    if (!response.ok) {
      console.error(`Supabase articles error: ${response.status}`);
      return filterArticles(getMockArticles(code), filters);
    }

    const data = await response.json();

    if (!Array.isArray(data)) {
      console.error('Unexpected response format from Supabase');
      return filterArticles(getMockArticles(code), filters);
    }

    return data
      .filter((signal: Record<string, any>) => signal.articles)
      .map(mapSignalToArticle);
  } catch (error) {
    console.error('Error fetching articles from Supabase:', error);
    return filterArticles(getMockArticles(code), filters);
  }
}
